import type { TFunction } from 'i18next';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { formatDate } from '@/lib/format';
import { THAI_TIME_ZONE, spanDays, thaiDay, thaiDayRange } from '@/lib/timezone';

import type { EvidenceWindow } from './sparky-types';

/** The window first answers are counted in: its school year, else its dates, else all history. */
export function evidenceWindowLabel(t: TFunction, window: EvidenceWindow | null | undefined) {
  if (window?.schoolYear) return t('sparky.window.school_year', { name: window.schoolYear });
  const from = window?.from ? formatDate(window.from, THAI_TIME_ZONE) : null;
  // The API's end is exclusive; the last day inside the window is the day before.
  const to = window?.to ? formatDate(new Date(Date.parse(window.to) - 1).toISOString(), THAI_TIME_ZONE) : null;
  if (from && to) return t('sparky.window.range', { from, to });
  if (from) return t('sparky.window.since', { from });
  if (to) return t('sparky.window.until', { to });
  return t('sparky.window.all');
}

/** A chosen evidence window as inclusive Thai school days, `YYYY-MM-DD`. */
export interface WindowDays {
  from: string;
  to: string;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The window a link asks for; a broken one is reported and ignored, so the report falls back to its default. */
export function windowDaysFromParams(params: URLSearchParams): { days: WindowDays | null; invalid: boolean } {
  const from = params.get('windowFrom');
  const to = params.get('windowTo');
  if (!from && !to) return { days: null, invalid: false };
  const span = from && to && ISO_DAY.test(from) && ISO_DAY.test(to) ? spanDays(from, to) : null;
  return span && span >= 1 ? { days: { from: from!, to: to! }, invalid: false } : { days: null, invalid: true };
}

/** The API's `windowFrom`/`windowTo` instants for the chosen days, or nothing for the default window. */
export function windowQuery(days: WindowDays | null | undefined): Record<string, string> {
  if (!days) return {};
  const range = thaiDayRange(days.from, days.to);
  return { windowFrom: range.from, windowTo: range.to };
}

/** The inclusive days a response's window covers, for prefilling custom dates. */
export function windowDays(window: EvidenceWindow | null | undefined): Partial<WindowDays> {
  return {
    from: window?.from ? thaiDay(new Date(window.from)) : undefined,
    to: window?.to ? thaiDay(new Date(Date.parse(window.to) - 1)) : undefined,
  };
}

/** The evidence window kept in the page's query string, so a shared link opens the same view. */
export function useEvidenceWindowParams() {
  const [params, setParams] = useSearchParams();
  const { days, invalid } = windowDaysFromParams(params);
  const setDays = useCallback(
    (next: WindowDays | null) =>
      setParams(previous => {
        const updated = new URLSearchParams(previous);
        if (next) {
          updated.set('windowFrom', next.from);
          updated.set('windowTo', next.to);
        } else {
          updated.delete('windowFrom');
          updated.delete('windowTo');
        }
        return updated;
      }),
    [setParams]
  );
  return { days, invalid, setDays };
}
